import { useEffect, useMemo, useState } from 'react'
import { publicArticlesApi, type PublicArticleSummary, type PublicArticlesPage } from './api'
import PublicFooter from '../PublicFooter'
import PublicHeader from '../PublicHeader'
import { syncArticlesSeo } from '../seo'
import { articlePath, articlesPath, localeMeta, type Locale } from '../i18n'
import { articlesCopy, type ArticlesCopy } from './copy'
import './articles.css'

const PAGE_SIZE = 12

type State =
  | { kind: 'loading' }
  | { kind: 'error' }
  | { kind: 'ready'; data: PublicArticlesPage }

function formatDate(value: string, locale: Locale) {
  return new Intl.DateTimeFormat(localeMeta[locale].htmlLang, { dateStyle: 'long' }).format(new Date(value))
}

function ArticleCard({ article, index, locale, copy }: { article: PublicArticleSummary; index: number; locale: Locale; copy: ArticlesCopy }) {
  const href = articlePath(locale, article.slug)
  return (
    <article className="article-card">
      <a className="article-card__media" href={href} aria-label={`${copy.catalog.read}: ${article.title}`}>
        {article.headerImageUrl
          ? <img src={article.headerImageUrl} alt="" width="720" height="405" loading="lazy" />
          : <span className="article-card__signal" aria-hidden="true"><i /><b>{String(index + 1).padStart(2, '0')}</b></span>}
      </a>
      <div className="article-card__body">
        <div className="article-card__meta"><span>{copy.catalog.guide.toUpperCase()} {String(index + 1).padStart(2, '0')}</span><time dateTime={article.publishedAt}>{formatDate(article.publishedAt, locale)}</time></div>
        <h2><a href={href}>{article.title}</a></h2>
        {article.excerpt && <p>{article.excerpt}</p>}
        <a className="article-card__link" href={href}>{copy.catalog.read.toUpperCase()} <span aria-hidden="true">↗</span></a>
      </div>
    </article>
  )
}

export default function ArticlesPage({ locale }: { locale: Locale }) {
  const copy = articlesCopy[locale]
  const hubPath = articlesPath(locale)
  const query = useMemo(() => new URLSearchParams(typeof window === 'undefined' ? '' : window.location.search), [])
  const requestedPage = useMemo(() => {
    const value = Number(query.get('page'))
    return Number.isSafeInteger(value) && value > 0 ? value : 1
  }, [query])
  const [state, setState] = useState<State>({ kind: 'loading' })

  useEffect(() => {
    syncArticlesSeo(locale)
    const controller = new AbortController()
    void (async () => {
      let data = await publicArticlesApi.list({ locale, limit: PAGE_SIZE, offset: (requestedPage - 1) * PAGE_SIZE }, controller.signal)
      const totalPages = Math.max(1, Math.ceil(data.pageInfo.total / PAGE_SIZE))

      if (requestedPage > totalPages) {
        if (data.pageInfo.total > 0) {
          data = await publicArticlesApi.list({ locale, limit: PAGE_SIZE, offset: (totalPages - 1) * PAGE_SIZE }, controller.signal)
        }
        if (typeof window !== 'undefined') {
          const normalizedUrl = new URL(window.location.href)
          if (totalPages === 1) normalizedUrl.searchParams.delete('page')
          else normalizedUrl.searchParams.set('page', String(totalPages))
          window.history.replaceState(window.history.state, '', `${normalizedUrl.pathname}${normalizedUrl.search}${normalizedUrl.hash}`)
        }
      }

      setState({ kind: 'ready', data })
    })()
      .catch((error: Error) => {
        if (error.name !== 'AbortError') setState({ kind: 'error' })
      })
    return () => controller.abort()
  }, [locale, requestedPage])

  const totalPages = state.kind === 'ready' ? Math.max(1, Math.ceil(state.data.pageInfo.total / PAGE_SIZE)) : 1
  const page = state.kind === 'ready'
    ? Math.min(Math.floor(state.data.pageInfo.offset / PAGE_SIZE) + 1, totalPages)
    : requestedPage

  return (
    <div className="articles-shell">
      <PublicHeader standalone active="articles" />
      <main>
        <section className="articles-hero">
          <div className="articles-hero__grid" aria-hidden="true" />
          <div className="articles-hero__copy">
            <span className="articles-eyebrow"><i /> {copy.hero.eyebrow.toUpperCase()}</span>
            <h1>{copy.hero.line1}<br /><strong>{copy.hero.line2}</strong></h1>
            <p>{copy.hero.intro}</p>
          </div>
          <aside className="articles-hero__index" aria-label="Knowledge base summary">
            <span>{copy.hero.knowledgeBase.toUpperCase()}</span>
            <strong>{state.kind === 'ready' ? String(state.data.pageInfo.total).padStart(2, '0') : '—'}</strong>
            <small>{copy.hero.publishedGuides.toUpperCase()}</small>
          </aside>
        </section>

        <section className="articles-catalog" aria-labelledby="articles-heading">
          <header>
            <div><span className="articles-eyebrow">{copy.catalog.latest.toUpperCase()}</span><h2 id="articles-heading">{copy.catalog.title}</h2></div>
            {state.kind === 'ready' && state.data.pageInfo.total > 0 && <p>{copy.catalog.page(page, totalPages)}</p>}
          </header>

          {state.kind === 'loading' && <div className="articles-state" aria-live="polite"><span className="articles-loader" /><h2>{copy.catalog.loading}</h2></div>}
          {state.kind === 'error' && <div className="articles-state"><h2>{copy.catalog.unavailableTitle}</h2><p>{copy.catalog.unavailableText}</p></div>}
          {state.kind === 'ready' && state.data.items.length === 0 && <div className="articles-state"><h2>{copy.catalog.emptyTitle}</h2><p>{copy.catalog.emptyText}</p></div>}
          {state.kind === 'ready' && state.data.items.length > 0 && (
            <div className="articles-grid">
              {state.data.items.map((article, index) => <ArticleCard article={article} index={(page - 1) * PAGE_SIZE + index} locale={locale} copy={copy} key={article.id} />)}
            </div>
          )}

          {state.kind === 'ready' && totalPages > 1 && (
            <nav className="articles-pagination" aria-label={copy.catalog.pagination}>
              {state.data.pageInfo.hasPreviousPage
                ? <a href={page <= 2 ? hubPath : `${hubPath}?page=${page - 1}`}>← {copy.catalog.previous.toUpperCase()}</a>
                : <span aria-disabled="true">← {copy.catalog.previous.toUpperCase()}</span>}
              <span>{String(page).padStart(2, '0')} / {String(totalPages).padStart(2, '0')}</span>
              {state.data.pageInfo.hasNextPage
                ? <a href={`${hubPath}?page=${page + 1}`}>{copy.catalog.next.toUpperCase()} →</a>
                : <span aria-disabled="true">{copy.catalog.next.toUpperCase()} →</span>}
            </nav>
          )}
        </section>
      </main>
      <PublicFooter standalone />
    </div>
  )
}
